// Workflow editor: list / create / run workflows (Slice 9).
//
// A first-cut editor: workflows are authored as JSON definitions (the schema in
// design/02-workflow-definition-schema.md), listed for the user + shared, run in-browser via the
// SDK with live step progress, and cloned from shared ones. A visual drag/connect canvas is a
// future enhancement (tracked in TODO); the JSON editor exercises the full create/run/clone path.
import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import GroupsIcon from "@mui/icons-material/Groups";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SearchIcon from "@mui/icons-material/Search";

import type { ActionInfo, Client, WorkflowSummary } from "../types";
import { MONO } from "../theme";
import { EmptyState, IconTile, LoadingRows, PageHeader } from "../components/ui";

const STARTER = JSON.stringify(
  {
    firstStepId: "s1",
    steps: {
      s1: { name: "step one", action: "app/action", config: null, nextStepId: null },
    },
  },
  null,
  2,
);

// Insert a step for `action` into the workflow definition JSON `text`, returning the new JSON text.
// Appends a step keyed s<N+1>, wires the previous last step's nextStepId to it (best-effort), and
// points firstStepId at it when the graph was empty, so clicking palette actions builds a linear
// chain the user can then rewire. If `text` isn't valid JSON we start a fresh definition rather than
// throwing, so the palette always does something sensible.
export function insertActionStep(text: string, action: ActionInfo): string {
  let def: { firstStepId?: string; steps?: Record<string, any> };
  try {
    const parsed = JSON.parse(text);
    def = parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    def = {};
  }
  const steps: Record<string, any> = def.steps && typeof def.steps === "object" ? def.steps : {};
  const ids = Object.keys(steps);
  const nextId = `s${ids.length + 1}`;
  steps[nextId] = { name: action.displayName || action.action, action: action.name, config: null, nextStepId: null };
  // Link the previous last step to the new one so repeated inserts form a chain.
  if (ids.length > 0) {
    const prev = steps[ids[ids.length - 1]];
    if (prev && typeof prev === "object" && prev.nextStepId == null) prev.nextStepId = nextId;
  }
  const firstStepId = def.firstStepId || nextId;
  return JSON.stringify({ ...def, firstStepId, steps }, null, 2);
}

// Render a step's output for the run log: compact JSON, truncated so a large payload can't blow up
// the log. `undefined` (e.g. an action that returned nothing) shows as "(no output)".
function formatOutput(value: unknown) {
  if (value === undefined) return "(no output)";
  let s;
  try {
    s = JSON.stringify(value);
  } catch {
    s = String(value);
  }
  return s.length > 200 ? s.slice(0, 200) + "…" : s;
}

export function Workflows({ client }: { client: Client }) {
  const [mine, setMine] = useState<WorkflowSummary[]>([]);
  const [shared, setShared] = useState<WorkflowSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [definitionText, setDefinitionText] = useState(STARTER);
  const [shareIt, setShareIt] = useState(false);
  const [runLog, setRunLog] = useState<string[]>([]);
  const [actions, setActions] = useState<ActionInfo[]>([]);
  const [actionQuery, setActionQuery] = useState("");
  const [loading, setLoading] = useState(true);

  // Load the action catalog once for the palette. Best-effort: a failure just leaves the palette
  // empty (the JSON editor still works), so we don't surface it as a blocking error.
  useEffect(() => {
    let active = true;
    client
      .listActions()
      .then((a) => active && setActions(a))
      .catch(() => active && setActions([]));
    return () => {
      active = false;
    };
  }, [client]);

  const refresh = useCallback(() => {
    setError(null);
    setLoading(true);
    Promise.all([client.workflow.list(), client.workflow.listShared()])
      .then(([m, s]) => {
        setMine(m.workflows || []);
        setShared(s.workflows || []);
      })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [client]);

  useEffect(refresh, [refresh]);

  async function create(e: FormEvent) {
    e.preventDefault();
    setError(null);
    let definition;
    try {
      definition = JSON.parse(definitionText);
    } catch {
      setError("definition is not valid JSON");
      return;
    }
    try {
      await client.workflow.create(name, definition, shareIt ? "SHARED" : "PRIVATE");
      setName("");
      refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  async function run(id: string) {
    setRunLog([`running ${id}…`]);
    try {
      // onStep fires as each step STARTS (input only). The engine returns {outputs, visited}; we
      // append each visited step's returned output after completion so the result is actually
      // visible (the run log previously showed only step names + a count).
      const { outputs, visited } = await client.workflow.run(id, {
        onStep: (s) => setRunLog((l) => [...l, `→ ${s.name || s.id} (${s.action})`]),
      });
      setRunLog((l) => [
        ...l,
        `done: ${visited.length} step(s)`,
        ...visited.map((stepId) => `  ${stepId} = ${formatOutput(outputs[stepId])}`),
      ]);
    } catch (e) {
      setRunLog((l) => [...l, `error: ${(e as Error).message}`]);
    }
  }

  function clone(wf: WorkflowSummary) {
    setName(`${wf.name} (copy)`);
    setDefinitionText(JSON.stringify(wf.definition, null, 2));
  }

  const visibleActions = useMemo(() => {
    const query = actionQuery.trim().toLowerCase();
    if (!query) return actions;
    return actions.filter((action) =>
      `${action.name} ${action.displayName} ${action.description}`.toLowerCase().includes(query),
    );
  }, [actions, actionQuery]);

  return (
    <Stack spacing={3.5}>
      <PageHeader
        title="Workflows"
        subtitle="Chain app actions into automated workflows. Run your own or clone a shared one."
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 500px" },
          gap: 4,
          alignItems: "start",
        }}
      >
        <Stack spacing={3.5} sx={{ minWidth: 0 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Your workflows
            </Typography>
            {loading ? (
              <LoadingRows rows={2} />
            ) : (
              <WorkflowList
                items={mine}
                onRun={run}
                empty={
                  <EmptyState
                    icon={<AccountTreeIcon />}
                    title="You have no workflows yet"
                    hint="Create a definition in the editor or add an action from the catalog."
                    dense
                  />
                }
              />
            )}
          </Box>

          <Box>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              Shared workflows
            </Typography>
            {loading ? (
              <LoadingRows rows={2} />
            ) : (
              <WorkflowList
                items={shared}
                onRun={run}
                onClone={clone}
                empty={
                  <EmptyState
                    icon={<GroupsIcon />}
                    title="None shared yet"
                    hint="Shared workflows from other users will appear here."
                    accent="info"
                    dense
                  />
                }
              />
            )}
          </Box>

          {runLog.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1.5 }}>
                Run log
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "#0d1117",
                  color: "#c9d1d9",
                  fontFamily: MONO,
                  fontSize: 13,
                  whiteSpace: "pre-wrap",
                }}
              >
                {runLog.join("\n")}
              </Box>
            </Box>
          )}
        </Stack>

        <Paper
          elevation={1}
          sx={{ p: { xs: 2.5, sm: 3 }, minWidth: 0, position: { lg: "sticky" }, top: { lg: 92 } }}
        >
          <Typography variant="h6">Create a workflow</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5, mb: 2.5 }}>
            Author a JSON definition, then create it.
          </Typography>
          <Stack component="form" spacing={2} onSubmit={create}>
            <TextField
              label="Workflow name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
            {actions.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: "grey.50" }}>
                <Stack spacing={1.25}>
                  <TextField
                    value={actionQuery}
                    onChange={(event) => setActionQuery(event.target.value)}
                    placeholder="Find an action"
                    aria-label="Find an action"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <Stack
                    direction="row"
                    spacing={0.75}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ maxHeight: 126, overflowY: "auto" }}
                  >
                    {visibleActions.map((action) => (
                      <Chip
                        key={action.name}
                        size="small"
                        label={action.displayName || action.name}
                        title={action.description || action.name}
                        onClick={() => setDefinitionText((text) => insertActionStep(text, action))}
                        sx={{ cursor: "pointer", bgcolor: "background.paper" }}
                        variant="outlined"
                      />
                    ))}
                    {visibleActions.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No matching actions.
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            )}
            <TextField
              label="Definition (JSON)"
              value={definitionText}
              onChange={(e) => setDefinitionText(e.target.value)}
              multiline
              minRows={10}
              fullWidth
              spellCheck={false}
              slotProps={{ input: { sx: { fontFamily: MONO, fontSize: 13 } } }}
            />
            <FormControlLabel
              control={<Switch checked={shareIt} onChange={(e) => setShareIt(e.target.checked)} />}
              label="Share with everyone"
            />
            <Button type="submit" variant="contained" startIcon={<AddIcon />} disabled={!name} fullWidth>
              Create workflow
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Stack>
  );
}

interface WorkflowListProps {
  items: WorkflowSummary[];
  onRun: (id: string) => void;
  onClone?: (wf: WorkflowSummary) => void;
  empty: ReactNode;
}

function WorkflowList({ items, onRun, onClone, empty }: WorkflowListProps) {
  if (items.length === 0) return <>{empty}</>;
  return (
    <Stack spacing={2}>
      {items.map((wf) => {
        const steps = Object.keys(wf.definition.steps || {}).length;
        return (
          <Paper key={wf.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <IconTile icon={<AccountTreeIcon />} accent="primary" size={40} iconFontSize={22} />
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" noWrap>
                  {wf.name}
                </Typography>
                <Chip
                  size="small"
                  icon={<LinearScaleIcon sx={{ fontSize: 14 }} />}
                  label={`${steps} step${steps === 1 ? "" : "s"}`}
                  sx={{ mt: 0.5, height: 22, bgcolor: "grey.100", "& .MuiChip-label": { fontSize: 12 } }}
                />
              </Box>
              <Stack direction="row" spacing={1}>
                {onClone && (
                  <Button size="small" variant="text" startIcon={<ContentCopyIcon />} onClick={() => onClone(wf)}>
                    Clone
                  </Button>
                )}
                <Button size="small" variant="contained" startIcon={<PlayArrowIcon />} onClick={() => onRun(wf.id)}>
                  Run
                </Button>
              </Stack>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}
